/**
 * 🔐 Evaluación de fuerza de contraseña.
 *
 * Reglas acordadas: mínimo 8 caracteres + al menos 3 de los 4 conjuntos
 * (mayúscula, minúscula, número, símbolo) para considerarse aceptable.
 */

export interface PasswordChecks {
  length: boolean;
  upper: boolean;
  lower: boolean;
  digit: boolean;
  symbol: boolean;
}

export interface PasswordEvaluation {
  /** 0‑5, cantidad de requisitos cumplidos. */
  score: number;
  checks: PasswordChecks;
  /** true si cumple el mínimo exigido para guardar. */
  isAcceptable: boolean;
  /** Etiqueta corta para mostrar junto a la barra. */
  label: 'Muy débil' | 'Débil' | 'Aceptable' | 'Fuerte' | 'Excelente';
}

const LABELS: PasswordEvaluation['label'][] = [
  'Muy débil',
  'Muy débil',
  'Débil',
  'Aceptable',
  'Fuerte',
  'Excelente',
];

export const evaluatePassword = (raw: string): PasswordEvaluation => {
  const value = raw ?? '';

  const checks: PasswordChecks = {
    length: value.length >= 8,
    upper: /[A-Z]/.test(value),
    lower: /[a-z]/.test(value),
    digit: /\d/.test(value),
    symbol: /[^A-Za-z0-9]/.test(value),
  };

  const score =
    (checks.length ? 1 : 0) +
    (checks.upper ? 1 : 0) +
    (checks.lower ? 1 : 0) +
    (checks.digit ? 1 : 0) +
    (checks.symbol ? 1 : 0);

  const variety =
    (checks.upper ? 1 : 0) +
    (checks.lower ? 1 : 0) +
    (checks.digit ? 1 : 0) +
    (checks.symbol ? 1 : 0);

  const isAcceptable = checks.length && variety >= 3;

  return {
    score,
    checks,
    isAcceptable,
    label: LABELS[score] ?? 'Muy débil',
  };
};

export const PASSWORD_RULES: { key: keyof PasswordChecks; label: string }[] = [
  { key: 'length', label: 'Al menos 8 caracteres' },
  { key: 'upper', label: 'Una letra mayúscula' },
  { key: 'lower', label: 'Una letra minúscula' },
  { key: 'digit', label: 'Un número' },
  { key: 'symbol', label: 'Un símbolo (!, @, #, …)' },
];
